import { useQuery } from "@apollo/client";
import { ICONS_MODULES_PATHS } from "components/Layout/Sidebar/components/body/content/utils/iconModulesPath";
import { Loader } from "components/ui/v2";
import SVG from "components/SVG";
import { ExplorePersonRowV2 } from "components/ui/v2/explore/bundle/ExplorePersonRowV2";
import SvgWithOutData from "components/core/SvgWithOutData/SvgWithOutData";
import { gqlEndorsement } from "gql";
import {
  IGetEndorsementInput,
  IGetEndorsementResponse
} from "gql/Endorsement/queries";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";

interface IEndorsementListProps {
  userId: string;
  skillId: string;
  skillName: string;
  publicProfile?: boolean;
  showInlineHeader?: boolean;
}

const EndorsementList = ({
  userId,
  skillId,
  skillName,
  publicProfile = false,
  showInlineHeader = true
}: IEndorsementListProps) => {
  const { t } = useTranslation("common");

  const { loading: endorsementLoading, data: endorsementData } = useQuery<
    IGetEndorsementResponse,
    { getEndorsementInput: IGetEndorsementInput }
  >(gqlEndorsement.queries.GET_ENDORSEMENTS, {
    variables: {
      getEndorsementInput: {
        userId,
        skillId
      }
    },
    fetchPolicy: "network-only"
  });

  const endorsements = endorsementData?.endorsements || [];
  const hasEndorsements = endorsements.length > 0;

  return (
    <div className="min-h-[360px] space-y-4 px-1 py-1">
      {showInlineHeader && (
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
            <SVG src={ICONS_MODULES_PATHS.CONFIG.SKILLS} width={18} height={18} />
          </div>
          <div className="min-w-0">
            <h2 className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]">
              {t("Valoraciones de")}{" "}
              <span className="capitalize text-indigo-600 dark:text-indigo-300">
                {skillName}
              </span>
            </h2>
            {hasEndorsements && (
              <p className="text-[9px] text-slate-500 dark:text-zinc-400">
                {endorsements.length} {t("validaciones")}
              </p>
            )}
          </div>
        </div>
      )}

      {endorsementLoading && (
        <div className="flex justify-center py-12">
          <Loader size="lg" />
        </div>
      )}

      {!endorsementLoading && hasEndorsements && (
        <div className="space-y-4">
          <h2 className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]">
            {t("Todas las validaciones")}
          </h2>
          <div className="grid gap-3">
            {endorsements.map((endorsement, index) => (
              <div
                key={endorsement.id || index}
                className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-zinc-800 dark:bg-zinc-900/60"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <ExplorePersonRowV2
                      user={endorsement.endorserUser}
                      secondaryLine={endorsement.endorserUser?.email}
                    />
                  </div>

                  <span className="inline-flex shrink-0 items-center rounded-full border border-slate-200/50 bg-slate-100/80 px-2 py-0.5 text-[9px] font-medium text-slate-600 dark:border-zinc-700/50 dark:bg-zinc-800/80 dark:text-zinc-300">
                    {moment(endorsement.createdAt).format("DD/MM/YYYY")}
                  </span>
                </div>

                {endorsement.comment && (
                  <div className="mt-3 rounded-xl bg-slate-50/90 p-3 dark:bg-zinc-900/50">
                    <p className="text-[9px] italic leading-snug text-slate-600 dark:text-zinc-400">
                      &ldquo;{endorsement.comment}&rdquo;
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!endorsementLoading && !hasEndorsements && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200/70 bg-white/70 px-4 py-12 text-center dark:border-zinc-800 dark:bg-zinc-900/60">
          <div className="mb-6">
            <SvgWithOutData />
          </div>

          <div className="space-y-3">
            {!publicProfile ? (
              <>
                <h3 className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]">
                  {t("Actualmente no tienes validaciones")}
                </h3>
                <p className="max-w-md text-[9px] leading-relaxed text-slate-600 dark:text-zinc-400">
                  {t(
                    "Agrega más habilidades para que te puedan validar pronto 😊"
                  )}
                </p>
                <span className="mt-4 inline-flex rounded-full bg-indigo-100 px-4 py-2 text-[11px] font-semibold text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">
                  {t("¡Comienza ahora!")}
                </span>
              </>
            ) : (
              <>
                <h3 className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]">
                  {t("Este usuario no tiene validaciones")}
                </h3>
                <p className="max-w-md text-[9px] leading-relaxed text-slate-600 dark:text-zinc-400">
                  {t("¡Sé el primero en validar sus habilidades!")}
                </p>
                <span className="mt-4 inline-flex rounded-full bg-emerald-100 px-4 py-2 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                  {t("Validar ahora")}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EndorsementList;
